import React, { useState } from "react"
import { useQuery } from "urql"

import LoadingSpinner from "../../components/shared/LoadingSpinner"
import GraphQLProvider from "../../providers/GraphQLProvider"
import SendIntakeFormButton from "../IntakeForms/SendIntakeFormButton"

import ClientPageView from "./ClientPageView"

const CLIENT_INTAKE_FORM_QUERY = `
  query IntakeForm($clientId: ID!) {
    client(clientId: $clientId) {
      id
      intakeForm { text time }
      intakeFormSchema
      intakeFormSubmitted
      intakeFormHasDataIssue
      intakeLastSentAt
    }
  }
`

const IntakeForm = ({ client: clientProp, upcomingAppointments, clientNotesEnabled }) => {
  const [client] = useState(clientProp)
  const [isConsentFieldsExpanded, setIsConsentFieldsExpanded] = useState(false)
  const [{ data, fetching }] = useQuery({
    query: CLIENT_INTAKE_FORM_QUERY,
    variables: { clientId: client.id },
    requestPolicy: "network-only"
  })

  const rawJson = data?.client?.intakeForm?.text
  const parsedJson = rawJson && JSON.parse(rawJson)
  const formSchema = data?.client?.intakeFormSchema && JSON.parse(data.client.intakeFormSchema)

  // Helper function to find field type in the schema
  const getFieldType = (fieldName) => {
    if (!formSchema?.pages) return null

    for (const page of formSchema.pages) {
      for (const element of page.elements) {
        if (element.type === "panel" && element.elements) {
          for (const panelElement of element.elements) {
            if (panelElement.name === fieldName || panelElement.title === fieldName) {
              return panelElement.type
            }
          }
        } else if (element.name === fieldName || element.title === fieldName) {
          return element.type
        }
      }
    }
    return null
  }

  // Helper function to get field configuration from schema
  const getFieldConfig = (fieldName) => {
    if (!formSchema?.pages) return null

    for (const page of formSchema.pages) {
      for (const element of page.elements) {
        if (element.type === "panel" && element.elements) {
          for (const panelElement of element.elements) {
            if (panelElement.name === fieldName || panelElement.title === fieldName) {
              return panelElement
            }
          }
        } else if (element.name === fieldName || element.title === fieldName) {
          return element
        }
      }
    }
    return null
  }

  // Helper function to render field value based on type
  const renderFieldValue = (fieldName, value) => {
    const fieldType = getFieldType(fieldName)
    const fieldConfig = getFieldConfig(fieldName)

    // Handle ranking type (used for disclaimers and consents)
    if (fieldType === "ranking" && Array.isArray(value)) {
      const allChoices = fieldConfig?.choices || []
      const hasUnrankedItems = allChoices.length > value.length

      return (
        <div className="ml-4">
          <div className="mb-2">
            <span className="text-sm font-semibold text-gray-700">Client&apos;s Priority Order:</span>
          </div>
          <ol className="list-decimal space-y-1 pl-5">
            {value.map((item, index) => (
              <li key={index} className="text-gray-900">
                {item}
              </li>
            ))}
          </ol>
          {hasUnrankedItems && (
            <div className="mt-3">
              <span className="text-sm font-semibold text-gray-600">Not Ranked:</span>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-gray-600">
                {allChoices
                  .filter((choice) => !value.includes(choice))
                  .map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
              </ul>
            </div>
          )}
          {allChoices.length > 0 && (
            <div className="mt-3 text-xs text-gray-500">
              Total items: {value.length} ranked out of {allChoices.length}
            </div>
          )}
        </div>
      )
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return (
        <ul className="list-inside list-disc">
          {value.map((item, index) =>
            typeof item === "object" ? (
              <li key={index}>
                Row {index + 1}:
                <ul className="ml-4 list-inside list-disc">
                  {Object.entries(item).map(([subKey, subValue]) => (
                    <li key={subKey}>
                      {subKey}: {subValue}
                    </li>
                  ))}
                </ul>
              </li>
            ) : (
              <li key={index}>{item}</li>
            )
          )}
        </ul>
      )
    }

    // Handle objects
    if (typeof value === "object") {
      return (
        <div>
          {Object.entries(value).map(([key, subValue]) => (
            <li key={key}>
              {key}:{" "}
              {typeof subValue === "object" ? (
                <ul className="ml-4 list-inside list-disc">
                  {Object.entries(subValue).map(([subKey, subSubValue]) => (
                    <li key={subKey}>
                      {subKey}: {subSubValue}
                    </li>
                  ))}
                </ul>
              ) : (
                subValue
              )}
            </li>
          ))}
        </div>
      )
    }

    // Handle images
    if (typeof value === "string" && value.startsWith("data:image")) {
      return <img src={value} alt="intake form image" className="max-h-[200px] max-w-[200px]" />
    }

    // Handle booleans and strings
    return <span>{value === true ? "Yes" : value === false ? "No" : value}</span>
  }

  // Helper function to get all ranking fields from schema
  const getRankingFieldsFromSchema = () => {
    if (!formSchema?.pages) return []

    const rankingFields = []
    for (const page of formSchema.pages) {
      for (const element of page.elements) {
        if (element.type === "panel" && element.elements) {
          for (const panelElement of element.elements) {
            if (panelElement.type === "ranking") {
              rankingFields.push({
                name: panelElement.name || panelElement.title,
                title: panelElement.title,
                choices: panelElement.choices || [],
                description: panelElement.description
              })
            }
          }
        } else if (element.type === "ranking") {
          rankingFields.push({
            name: element.name || element.title,
            title: element.title,
            choices: element.choices || [],
            description: element.description
          })
        }
      }
    }
    return rankingFields
  }

  const rankingFieldsInTemplate = getRankingFieldsFromSchema()

  return (
    <ClientPageView
      client={client}
      upcomingAppointments={upcomingAppointments}
      clientNotesEnabled={clientNotesEnabled}
      path={`/portal/clients/${client.id}/intake_form`}>
      {fetching ? (
        <LoadingSpinner />
      ) : data?.client?.intakeFormHasDataIssue ? (
        <div className="rounded-md bg-yellow-100 p-4">
          <div className="flex">
            <div className="shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Data Issue Detected</h3>
              <div className="mt-2 text-sm text-yellow-800">
                <p>This client submitted an intake form, but there is an issue accessing the data.</p>
                <p className="mt-2">Please contact support if you need to recover this information.</p>
              </div>
            </div>
          </div>
        </div>
      ) : parsedJson ? (
        <div className="mb-6">
          {/* Show ranking fields template info if they exist */}
          {rankingFieldsInTemplate.length > 0 && (
            <div className="mb-6 rounded-lg border border-blue-300">
              <button
                onClick={() => setIsConsentFieldsExpanded(!isConsentFieldsExpanded)}
                className="flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-blue-50">
                <div>
                  <h3 className="text-lg font-semibold text-blue-800">📋 Consent Fields in This Form</h3>
                  <p className="mt-1 text-sm text-blue-700">
                    Your intake form includes {rankingFieldsInTemplate.length} field(s) for consent and priority
                    assessment
                  </p>
                </div>
                <svg
                  className={`h-5 w-5 shrink-0 text-blue-800 transition-transform ${
                    isConsentFieldsExpanded ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isConsentFieldsExpanded && (
                <div className="border-t border-blue-300 p-4 pt-3">
                  {rankingFieldsInTemplate.map((field, idx) => {
                    // Helper function to format text with line breaks and bullets
                    const formatText = (text) => {
                      if (!text) return null
                      // Split by line breaks
                      const lines = text.split(/\n|\\n/)
                      return lines.map((line, i) => {
                        // Check if line starts with bullet point
                        if (line.trim().startsWith("•") || line.trim().startsWith("*")) {
                          return (
                            <div key={i} className="mb-1 flex">
                              <span className="mr-2">•</span>
                              <span>{line.replace(/^[•*]\s*/, "").trim()}</span>
                            </div>
                          )
                        }
                        // Regular line with line break
                        return line.trim() ? (
                          <div key={i} className="mb-2">
                            {line.trim()}
                          </div>
                        ) : null
                      })
                    }

                    return (
                      <div key={idx} className="mb-4 rounded border border-blue-300 bg-white p-3 last:mb-0">
                        <div className="mb-2">
                          {formatText(field.title) || <h4 className="font-semibold text-gray-900">{field.title}</h4>}
                        </div>
                        {field.description && (
                          <div className="mb-2 text-sm text-gray-600">{formatText(field.description)}</div>
                        )}
                        <div className="text-sm text-gray-700">
                          {field.choices.length > 0 && (
                            <span className="font-medium">Available options ({field.choices.length}):</span>
                          )}
                          <ul className="mt-1 list-inside list-disc pl-2">
                            {field.choices.map((choice, i) => (
                              <li key={i} className="text-gray-600">
                                {choice}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Show client responses */}
          <h3 className="mb-4 text-lg font-semibold text-gray-900">Client&apos;s Responses</h3>
          {Object.entries(parsedJson).map(([fieldName, value]) => (
            <div key={fieldName} className="mb-2 flex flex-col">
              <span className="font-bold">{fieldName.replace("-Comment", " (Comment)")}</span>
              {renderFieldValue(fieldName, value)}
            </div>
          ))}
        </div>
      ) : (
        <div>
          <p className="mb-4">
            {data?.client?.intakeLastSentAt
              ? "This client has not submitted their intake form."
              : "This client has not been sent an intake form."}
          </p>
          <SendIntakeFormButton clientId={client.id} intakeLastSentAt={data?.client?.intakeLastSentAt} />
        </div>
      )}
    </ClientPageView>
  )
}

export default function ClientIntakeForm(props) {
  return (
    <GraphQLProvider>
      <IntakeForm {...props} />
    </GraphQLProvider>
  )
}
